import { logger } from "@bfc-software/app-toolbox";
import createFetchClient from "openapi-fetch";
import { getEnv } from "@bfc-software/bfc-web-ui/environment";

/**
 * Hand-written stand-in for generated types. `@bfc-software/api-schemas` generates
 * from a committed swagger json, and this service has none yet. The canonical spec
 * is in `bfc_go_services/analytics_service/spec/oapi`.
 *
 * To replace this type:
 *
 * - Add `fetch:analytics` and `generate:analytics` scripts to
 *   `@bfc-software/api-schemas`.
 * - Export `AnalyticsSchemaTypes`.
 * - Change the generic parameter below, and delete this type.
 */
export type AnalyticsPaths = {
  "/guest-token": {
    post: {
      responses: {
        200: {
          content: {
            "application/json": {
              data: {
                /** The Preset guest token. Roughly a 5-minute life. */
                token: string;
                expires_at: string;
                /** For example, https://bfc.us1a.app.preset.io */
                superset_domain: string;
                /** The uuid of the embedded Preset dashboard. */
                dashboard_id: string;
              };
            };
          };
        };
        401: { content: { "application/json": { message?: string } } };
        403: { content: { "application/json": { message?: string } } };
        /** The organization has no entitlement mapping. */
        404: { content: { "application/json": { message?: string } } };
        500: { content: { "application/json": { message?: string } } };
        502: { content: { "application/json": { message?: string } } };
        503: { content: { "application/json": { message?: string } } };
      };
    };
  };
};

const fetchClient = () => {
  const token =
    localStorage.getItem("aaaToken") || localStorage.getItem("token");

  const baseUrl = `${getEnv().analyticsServiceBase}/analytics/v1`;

  logger.info(`Building analytics api client against '${baseUrl}'`);

  return createFetchClient<AnalyticsPaths>({
    baseUrl,
    credentials: "include",
    headers: {
      "X-ACCESS-KEY": getEnv().authAccessKey,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
};

/**
 * Raw fetch client for imperative use. Call it per request. `fetchClient` reads the
 * aaaToken from localStorage at construction, so a client built once at mount keeps
 * sending a stale bearer for the life of the tab.
 *
 * No `apiClient` or QueryClient here, unlike the sibling clients. The guest-token
 * endpoint must not go through react-query. The SDK owns the refresh schedule, and
 * a cached guest token served after it expires is the failure this prevents. Add
 * both if a cacheable endpoint appears.
 */
export const rawFetchClient = () => fetchClient();
